import { useState } from 'react';
import { motion } from 'framer-motion';
import { Bus, Plus, Wand2, Users, CheckCircle2, Clock, MapPin, PlayCircle } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Button,
  Modal,
  Field,
  Input,
  Select,
  Badge,
  StatusBadge,
  Loader,
  EmptyState,
  SectionTitle,
  Avatar,
  ConfirmModal,
  label,
} from '../../components/UI';
import { fmtDateTime, fmtTime, pct } from '../../utils/format';

const BUS_BLANK = {
  busNumber: '', busName: '', capacity: 40, tripType: 'venue_shuttle',
  routeName: '', from: '', to: '', departureAt: '', boardingPoint: '',
  driverName: '', driverMobile: '',
};

const TRIP_TYPES = [
  ['airport_pickup', 'Airport pickup'],
  ['hotel_transfer', 'Hotel transfer'],
  ['venue_shuttle', 'Venue shuttle'],
  ['excursion', 'Excursion'],
  ['airport_drop', 'Airport drop'],
];

export default function Transport() {
  const { eventId, events } = useEvents();
  const { isCorporate } = useAuth();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BUS_BLANK);
  const [saving, setSaving] = useState(false);
  const [manifestFor, setManifestFor] = useState(null);
  const [departFor, setDepartFor] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const { data: buses, loading, reload } = useFetch(
    '/buses', { event: eventId, limit: 50 }, { skip: !eventId },
  );

  if (!events.length) {
    return <EmptyState message="No events available" hint="Transport planning begins once you have an event." icon={Bus} />;
  }

  const totals = (buses || []).reduce((a, b) => ({
    capacity: a.capacity + b.capacity,
    seats: a.seats + b.passengers.length,
    boarded: a.boarded + (b.boardedCount || 0),
  }), { capacity: 0, seats: 0, boarded: 0 });

  const create = async () => {
    if (!form.busNumber.trim()) return toast.error('Bus number is required');
    setSaving(true);
    try {
      await post('/buses', {
        event: eventId,
        busNumber: form.busNumber.toUpperCase(),
        busName: form.busName,
        capacity: Number(form.capacity) || 40,
        tripType: form.tripType,
        route: { name: form.routeName, from: form.from, to: form.to },
        departureAt: form.departureAt || undefined,
        boardingPoint: form.boardingPoint,
        driver: { name: form.driverName, mobile: form.driverMobile },
      });
      toast.success(`Bus ${form.busNumber} added`);
      setOpen(false);
      setForm(BUS_BLANK);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const autoAllocate = async () => {
    setAutoBusy(true);
    try {
      const res = await post('/buses/auto-allocate', { event: eventId });
      toast.success(res.message);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAutoBusy(false);
    }
  };

  const depart = async () => {
    try {
      const res = await post(`/buses/${departFor._id}/depart`, {});
      toast.success(res.message);
      setDepartFor(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <SectionTitle
        title="Bus & transport"
        subtitle={isCorporate
          ? 'Your employees\' coaches, seats and live boarding status'
          : 'Coaches, seat allocation and live boarding status'}
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            {/* Transport is built by the organiser; a company admin follows it. */}
            {!isCorporate && (
              <>
                <Button variant="ghost" icon={Wand2} onClick={autoAllocate} loading={autoBusy} disabled={!buses?.length}>
                  Auto-allocate seats
                </Button>
                <Button icon={Plus} onClick={() => { setForm(BUS_BLANK); setOpen(true); }} disabled={!eventId}>
                  Add bus
                </Button>
              </>
            )}
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Coaches" value={buses?.length || 0} icon={Bus} tone="gold" />
        <Stat label="Total Capacity" value={totals.capacity} icon={Users} tone="info" />
        <Stat label="Seats Allocated" value={totals.seats} icon={CheckCircle2} tone="warning" progress={pct(totals.seats, totals.capacity)} />
        <Stat label="Boarded" value={totals.boarded} icon={PlayCircle} tone="success" progress={pct(totals.boarded, totals.seats)} />
      </div>

      {loading ? <Loader label="Loading coaches" /> : !buses?.length ? (
        <EmptyState
          message="No coaches added yet"
          hint={isCorporate
            ? 'Your event coordinator adds the coaches. They will appear here with your employees\' seats once they do.'
            : 'Add your buses with their capacity and route, then auto-allocate passengers across them.'}
          icon={Bus}
          action={isCorporate ? null : <Button icon={Plus} onClick={() => setOpen(true)}>Add first bus</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 15 }}>
          {buses.map((b, i) => (
            <motion.div key={b._id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Card hover padding={18} style={{ height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 11, alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 'var(--r-sm)',
                        background: 'rgba(239,212,138,0.11)', display: 'grid', placeItems: 'center',
                      }}>
                        <Bus size={16} color="var(--gold-400)" />
                      </div>
                      <div>
                        <h3 style={{ fontSize: 14.5, fontWeight: 600 }}>{b.busNumber}</h3>
                        {b.busName && <p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{b.busName}</p>}
                      </div>
                    </div>
                  </div>
                  <StatusBadge status={b.status} />
                </div>

                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.3, color: 'var(--text-mid)' }}>
                  {b.route?.name && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MapPin size={13} color="var(--gold-600)" /> {b.route.name}
                    </span>
                  )}
                  {b.departureAt && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Clock size={13} color="var(--gold-600)" /> Departs {fmtDateTime(b.departureAt)}
                    </span>
                  )}
                  <Badge tone="neutral">{TRIP_TYPES.find(([v]) => v === b.tripType)?.[1] || label(b.tripType)}</Badge>
                </div>

                {/* boarding progress */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-mid)' }}>
                      <strong style={{ color: 'var(--ok)' }}>{b.boardedCount || 0}</strong> boarded ·{' '}
                      <strong style={{ color: 'var(--warn)' }}>{b.pendingCount || 0}</strong> pending
                    </span>
                    <span style={{ color: 'var(--text-lo)' }}>{b.passengers?.length || 0}/{b.capacity}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct(b.boardedCount || 0, b.capacity)}%` }}
                      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                      style={{ background: 'linear-gradient(90deg, var(--ok), #86EFAC)' }}
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct(b.pendingCount || 0, b.capacity)}%` }}
                      transition={{ duration: 0.85, delay: 0.1 }}
                      style={{ background: 'var(--warn)', opacity: 0.55 }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 15 }}>
                  <Button size="sm" variant="ghost" style={{ flex: 1 }} onClick={() => setManifestFor(b)}>
                    Manifest
                  </Button>
                  {b.status !== 'departed' && b.status !== 'arrived' && (
                    <Button size="sm" variant="outline" onClick={() => setDepartFor(b)}>Depart</Button>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <ManifestModal bus={manifestFor} onClose={() => setManifestFor(null)} />

      <ConfirmModal
        open={Boolean(departFor)} onClose={() => setDepartFor(null)} onConfirm={depart}
        title={`Mark ${departFor?.busNumber} as departed?`} confirmLabel="Mark departed"
        message={`${departFor?.pendingCount || 0} passenger(s) have not boarded and will be recorded as no-shows. This closes boarding for this coach.`}
      />

      <Modal
        open={open} onClose={() => setOpen(false)} width={600}
        title="Add a coach"
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} loading={saving}>Add bus</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr', gap: 12 }}>
            <Field label="Bus number" required>
              <Input value={form.busNumber} onChange={(e) => setForm({ ...form, busNumber: e.target.value })} placeholder="DL01GL1001" autoFocus />
            </Field>
            <Field label="Coach name">
              <Input value={form.busName} onChange={(e) => setForm({ ...form, busName: e.target.value })} placeholder="Coach Alpha" />
            </Field>
            <Field label="Capacity">
              <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
            </Field>
          </div>

          <Field label="Trip type">
            <Select value={form.tripType} onChange={(e) => setForm({ ...form, tripType: e.target.value })}>
              {TRIP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>

          <Field label="Route name">
            <Input value={form.routeName} onChange={(e) => setForm({ ...form, routeName: e.target.value })} placeholder="IGI T3 → The Leela Palace" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="From"><Input value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} placeholder="IGI Airport T3" /></Field>
            <Field label="To"><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="The Leela Palace" /></Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Departure time"><Input type="datetime-local" value={form.departureAt} onChange={(e) => setForm({ ...form, departureAt: e.target.value })} /></Field>
            <Field label="Boarding point"><Input value={form.boardingPoint} onChange={(e) => setForm({ ...form, boardingPoint: e.target.value })} placeholder="T3 Arrivals, Gate 5" /></Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Driver name"><Input value={form.driverName} onChange={(e) => setForm({ ...form, driverName: e.target.value })} /></Field>
            <Field label="Driver mobile"><Input value={form.driverMobile} onChange={(e) => setForm({ ...form, driverMobile: e.target.value })} /></Field>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ManifestModal({ bus, onClose }) {
  const { data, loading } = useFetch(
    bus ? `/buses/${bus._id}/manifest` : null, {}, { skip: !bus },
  );

  return (
    <Modal
      open={Boolean(bus)} onClose={onClose} width={620}
      title={`Manifest — ${bus?.busNumber || ''}`}
      subtitle={bus?.route?.name}
    >
      {loading ? <Loader /> : data && (
        <>
          <div style={{ display: 'flex', gap: 22, marginBottom: 20, flexWrap: 'wrap' }}>
            <Metric label="Total" value={data.summary.total} />
            <Metric label="Boarded" value={data.summary.boarded} tone="var(--ok)" />
            <Metric label="Pending" value={data.summary.pending} tone="var(--warn)" />
            <Metric label="No-show" value={data.summary.noShow} tone="var(--danger)" />
          </div>

          {[
            ['Boarded', data.boarded, 'var(--ok)'],
            ['Yet to board', data.pending, 'var(--warn)'],
            ['No-show', data.noShow, 'var(--danger)'],
          ].filter(([, list]) => list?.length).map(([title, list, tone]) => (
            <div key={title} style={{ marginBottom: 20 }}>
              <p style={{
                fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.9,
                color: tone, marginBottom: 10,
              }}>
                {title} ({list.length})
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {list.map((p) => (
                  <div key={p.user?._id} style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px',
                    background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r)',
                  }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                      background: 'var(--ink-700)', display: 'grid', placeItems: 'center',
                      fontSize: 11, fontWeight: 700, color: 'var(--gold-400)',
                    }}>
                      {p.seatNumber}
                    </span>
                    <Avatar name={p.user?.name} size={26} vip={p.user?.isVip} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.8, fontWeight: 500 }}>{p.user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{p.user?.designation || p.user?.mobile}</div>
                    </div>
                    {p.boardedAt && <span style={{ fontSize: 11, color: 'var(--ok)' }}>{fmtTime(p.boardedAt)}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!data.summary.total && <EmptyState message="No passengers allocated to this coach yet" icon={Users} />}
        </>
      )}
    </Modal>
  );
}

function Metric({ label: text, value, tone = 'var(--gold-400)' }) {
  return (
    <div>
      <p style={{ fontSize: 22, fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 2 }}>{text}</p>
    </div>
  );
}
